import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  X,
  Users,
  MessageSquare,
  Send,
  Globe,
  Search,
  AlertTriangle,
  Ban
} from "lucide-react";
import type { AdminStats } from "@/lib/types";

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
}

export function AdminPanel({ isOpen, onClose, isAdmin }: AdminPanelProps) {
  const [stats, setStats] = useState<AdminStats>({
    activeUsers: 0,
    activeChats: 0,
    messagesDaily: 0,
    translationsCount: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && isAdmin) {
      loadStats();
    }
  }, [isOpen, isAdmin]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('GET', '/api/admin/stats');
      const statsData = await response.json();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast({
        title: "Error",
        description: "Failed to load admin statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const blockUser = async (uid: string) => {
    try {
      await apiRequest('POST', `/api/admin/users/${uid}/block`);
      toast({
        title: "User Blocked",
        description: "User has been blocked successfully",
      });
      // TODO: Refresh user list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to block user",
        variant: "destructive",
      });
    }
  };

  const warnUser = (uid: string) => {
    // TODO: Implement user warning
    toast({
      title: "Warning Sent",
      description: "Warning has been sent to the user",
    });
  };

  if (!isOpen || !isAdmin) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-background z-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6">
        {/* Admin Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground">Vyzle Chat Management</p>
          </div>
          <Button
            onClick={onClose}
            variant="secondary"
            className="flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Close Admin</span>
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-r from-[hsl(128,60%,54%)] to-[hsl(135,71%,57%)] text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80">Active Users</p>
                  <p className="text-3xl font-bold">
                    {loading ? "..." : stats.activeUsers.toLocaleString()}
                  </p>
                </div>
                <Users className="w-8 h-8 text-white/80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-[hsl(207,90%,54%)] to-purple-500 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80">Active Chats</p>
                  <p className="text-3xl font-bold">
                    {loading ? "..." : stats.activeChats.toLocaleString()}
                  </p>
                </div>
                <MessageSquare className="w-8 h-8 text-white/80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80">Messages Today</p>
                  <p className="text-3xl font-bold">
                    {loading ? "..." : stats.messagesDaily.toLocaleString()}
                  </p>
                </div>
                <Send className="w-8 h-8 text-white/80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-400 to-blue-500 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80">Translations</p>
                  <p className="text-3xl font-bold">
                    {loading ? "..." : stats.translationsCount.toLocaleString()}
                  </p>
                </div>
                <Globe className="w-8 h-8 text-white/80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Management */}
        <Card>
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              User Management
            </h2>
            <div className="flex space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="reported">Reported</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-muted">
                <tr>
                  <th className="p-4 font-medium text-foreground">User ID</th>
                  <th className="p-4 font-medium text-foreground">Status</th>
                  <th className="p-4 font-medium text-foreground">Chats</th>
                  <th className="p-4 font-medium text-foreground">Messages</th>
                  <th className="p-4 font-medium text-foreground">Joined</th>
                  <th className="p-4 font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {/* Mock data - replace with real data */}
                <tr>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-[hsl(128,60%,54%)] rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-mono text-sm">Vyzle_7B3A</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge className="bg-[hsl(135,71%,57%)] text-white">
                      Online
                    </Badge>
                  </td>
                  <td className="p-4 text-foreground">3</td>
                  <td className="p-4 text-foreground">127</td>
                  <td className="p-4 text-muted-foreground">2 days ago</td>
                  <td className="p-4">
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => warnUser("Vyzle_7B3A")}
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Warn
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => blockUser("Vyzle_7B3A")}
                      >
                        <Ban className="w-3 h-3 mr-1" />
                        Block
                      </Button>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-mono text-sm">Explorer_9F2X</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge variant="secondary">Away</Badge>
                  </td>
                  <td className="p-4 text-foreground">1</td>
                  <td className="p-4 text-foreground">45</td>
                  <td className="p-4 text-muted-foreground">1 week ago</td>
                  <td className="p-4">
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => warnUser("Explorer_9F2X")}
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Warn
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => blockUser("Explorer_9F2X")}
                      >
                        <Ban className="w-3 h-3 mr-1" />
                        Block
                      </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
